/**
 * Eximtech - Design System
 *
 * Design tokens and utility functions for consistent UI styling
 * Following the "Command Center" layout pattern for logistics/B2B applications
 */

// =============================================================================
// COLOR PALETTE
// =============================================================================

export const colors = {
  // Primary Brand Colors
  primary: {
    main: 'rgb(5, 150, 105)',      // emerald-600 - Main CTA, highlights
    hover: 'rgb(4, 120, 87)',       // emerald-700 - Hover states
    light: 'rgba(5, 150, 105, 0.1)', // emerald-600 with opacity
  },

  // Secondary/Accent Colors
  secondary: {
    cyan: 'rgb(8, 145, 178)',       // cyan-600 - Shippers
    orange: 'rgb(234, 88, 12)',     // orange-600 - Freight Forwarders
    blue: 'rgb(37, 99, 235)',       // blue-600 - General accents
  },

  // Status Indicators (Activity-based)
  status: {
    active: {
      bg: 'rgb(16, 185, 129)',      // emerald-500 - Active (<7 days)
      text: 'rgb(4, 120, 87)',      // emerald-700
      light: 'rgb(209, 250, 229)',  // emerald-100
    },
    warning: {
      bg: 'rgb(245, 158, 11)',      // amber-500 - Warning (7-30 days)
      text: 'rgb(180, 83, 9)',      // amber-700
      light: 'rgb(254, 243, 199)',  // amber-100
    },
    inactive: {
      bg: 'rgb(239, 68, 68)',       // red-500 - Inactive (>30 days)
      text: 'rgb(185, 28, 28)',     // red-700
      light: 'rgb(254, 226, 226)',  // red-100
    },
    neutral: {
      bg: 'rgb(107, 114, 128)',     // gray-500
      text: 'rgb(55, 65, 81)',      // gray-700
      light: 'rgb(243, 244, 246)',  // gray-100
    },
  },

  // Role-based Colors
  roles: {
    master: {
      bg: 'rgb(243, 232, 255)',     // violet-100
      text: 'rgb(109, 40, 217)',    // violet-700
      icon: 'rgb(139, 92, 246)',    // violet-600
    },
    admin: {
      bg: 'rgb(219, 234, 254)',     // blue-100
      text: 'rgb(29, 78, 216)',     // blue-700
      icon: 'rgb(37, 99, 235)',     // blue-600
    },
    user: {
      bg: 'rgb(243, 244, 246)',     // gray-100
      text: 'rgb(55, 65, 81)',      // gray-700
      icon: 'rgb(107, 114, 128)',   // gray-500
    },
  },

  // Subscription Plan Colors
  plans: {
    enterprise: {
      bg: 'rgb(243, 232, 255)',     // violet-100
      text: 'rgb(109, 40, 217)',    // violet-700
    },
    professional: {
      bg: 'rgb(219, 234, 254)',     // blue-100
      text: 'rgb(29, 78, 216)',     // blue-700
    },
    basic: {
      bg: 'rgb(243, 244, 246)',     // gray-100
      text: 'rgb(55, 65, 81)',      // gray-700
    },
  },

  // Neutral Colors
  neutral: {
    white: 'rgb(255, 255, 255)',
    gray: {
      50: 'rgb(249, 250, 251)',
      100: 'rgb(243, 244, 246)',
      200: 'rgb(229, 231, 235)',
      300: 'rgb(209, 213, 219)',
      400: 'rgb(156, 163, 175)',
      500: 'rgb(107, 114, 128)',
      600: 'rgb(75, 85, 99)',
      700: 'rgb(55, 65, 81)',
      800: 'rgb(31, 41, 55)',
      900: 'rgb(17, 24, 39)',
    },
  },
}

// =============================================================================
// TAILWIND CLASS GENERATORS
// =============================================================================

/**
 * Get badge classes for user roles
 */
export const getRoleBadgeClasses = (role: 'master' | 'admin' | 'user'): string => {
  const classes = {
    master: 'bg-violet-100 text-violet-700',
    admin: 'bg-blue-100 text-blue-700',
    user: 'bg-gray-100 text-gray-700',
  }
  return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes[role]}`
}

/**
 * Get badge classes for subscription plans
 */
export const getPlanBadgeClasses = (plan: 'Enterprise' | 'Professional' | 'Basic'): string => {
  const classes = {
    Enterprise: 'bg-violet-100 text-violet-700',
    Professional: 'bg-blue-100 text-blue-700',
    Basic: 'bg-gray-100 text-gray-700',
  }
  return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${classes[plan]}`
}

/**
 * Get status text classes
 */
export const getStatusTextClasses = (isActive: boolean): string => {
  return isActive ? 'text-emerald-700' : 'text-gray-500'
}

/**
 * Get status badge classes
 */
export const getStatusBadgeClasses = (status: 'active' | 'inactive'): string => {
  return status === 'active'
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-gray-100 text-gray-700'
}

/**
 * Get company type badge classes (Shipper/Freight Forwarder)
 */
export const getCompanyTypeBadgeClasses = (type: 'shipper' | 'forwarder'): string => {
  return type === 'shipper'
    ? 'font-medium text-cyan-700'
    : 'font-medium text-orange-700'
}

// =============================================================================
// STATUS INDICATOR COMPONENTS
// =============================================================================

/**
 * Get activity status indicator styles based on last activity
 * @param lastActivity - ISO date string of last activity
 * @returns Object with className and title for the indicator dot
 */
export const getActivityIndicator = (lastActivity: string) => {
  const daysSince = Math.floor(
    (new Date().getTime() - new Date(lastActivity).getTime()) / (1000 * 60 * 60 * 24)
  )

  if (daysSince <= 7) {
    return {
      className: 'w-2 h-2 rounded-full bg-emerald-500',
      title: 'Ativo (<7 dias)',
    }
  }
  if (daysSince <= 30) {
    return {
      className: 'w-2 h-2 rounded-full bg-amber-500',
      title: 'Moderado (7-30 dias)',
    }
  }
  return {
    className: 'w-2 h-2 rounded-full bg-red-500',
    title: 'Inativo (>30 dias)',
  }
}

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export const typography = {
  // Page Headers
  pageTitle: 'text-2xl font-semibold text-gray-900',
  sectionTitle: 'text-lg font-semibold text-gray-900',
  cardTitle: 'text-xs font-medium text-gray-500 uppercase tracking-wide',

  // Metrics
  metricValue: 'text-3xl font-bold text-gray-900',
  metricValueAccent: 'text-3xl font-bold text-emerald-600',
  metricLabel: 'text-sm text-gray-600',

  // Table
  tableHeader: 'text-xs font-medium text-gray-500 uppercase tracking-wider',
  tableCell: 'text-sm text-gray-900',
  tableCellSecondary: 'text-sm text-gray-600',
  tableCellMono: 'text-sm text-gray-600 font-mono',

  // Body
  body: 'text-sm text-gray-700',
  bodySecondary: 'text-sm text-gray-500',
  caption: 'text-xs text-gray-600',
}

// =============================================================================
// SPACING
// =============================================================================

export const spacing = {
  // Container
  containerPadding: 'px-4 sm:px-6 lg:px-8',
  containerMaxWidth: 'max-w-7xl mx-auto',

  // Sections
  sectionGap: 'gap-6',
  sectionMarginBottom: 'mb-6',

  // Cards
  cardPadding: 'p-6',
  cardGap: 'gap-4',

  // Table
  tableCellPadding: 'px-4 py-3',
  tableHeaderPadding: 'px-4 py-3',
}

// =============================================================================
// BORDERS & SHADOWS
// =============================================================================

export const borders = {
  default: 'border border-gray-200',
  light: 'border border-gray-100',
  divider: 'border-b border-gray-200',
  rounded: 'rounded-lg',
  roundedFull: 'rounded-full',
}

export const shadows = {
  sm: 'shadow-sm',
  default: 'shadow',
  hover: 'hover:shadow-md transition-shadow',
}

// =============================================================================
// COMPONENT PRESETS
// =============================================================================

export const components = {
  // Card
  card: 'bg-white border border-gray-200 rounded-lg p-6',
  cardCompact: 'bg-white border border-gray-200 rounded-lg p-4',

  // Filter Bar
  filterBar: 'bg-gray-50 border border-gray-200 rounded-lg p-4',
  filterInput: 'px-3 h-9 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500',

  // Table
  tableContainer: 'bg-white border border-gray-200 rounded-lg overflow-hidden',
  tableHeader: 'bg-gray-50 border-b border-gray-200',
  tableRow: 'hover:bg-gray-50 transition-colors',
  tableRowSelected: 'bg-blue-50',
  tableDivider: 'divide-y divide-gray-100',

  // Footer
  tableFooter: 'bg-gray-50 px-4 py-3 border-t border-gray-200',

  // Avatar
  avatar: 'w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0',
  avatarText: 'text-xs font-medium text-emerald-700',

  // Button Icon
  iconButton: 'h-8 w-8 p-0',
  iconButtonDanger: 'h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50',
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get initials from a name
 */
export const getInitials = (name: string): string => {
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

/**
 * Format date to Brazilian format
 * @deprecated Use formatDateBR from '@/lib/date' instead for proper timezone handling
 */
export { formatDateBR, daysSince } from '../lib/date'
